/**
 * AB#21966 — the page someone was on their way to when they had to sign in.
 *
 * A link from the team chat email opens `/dashboard?order_id=…&tab=team_chat`.
 * Signed out, the admin sent them to the login and the login brought them back
 * to the home page: the order was lost and they landed on Needs Attention.
 *
 * The link is kept in sessionStorage: it stays with the browser tab across the
 * trip to the login (another site) and back, it survives the localStorage.clear()
 * of a logout, and it never leaks into another tab. It is used once, and only
 * while fresh, so an old link cannot hijack a later sign-in.
 */
export const RETURN_LINK_KEY = 'return_link';
export const RETURN_LINK_TTL_MS = 30 * 60 * 1000;

const SIGN_IN_PARAMS = [
  'access_token',
  'refresh_token',
  'impersonated',
  'imp_by_name',
  'imp_by_email',
  'imp_by_role',
  'imp_started_at',
];

/** An in-app path worth coming back to: not the home page, not a sign-in handoff. */
export const cleanReturnLink = (fullPath: unknown): string | null => {
  if (typeof fullPath !== 'string' || !fullPath.startsWith('/') || fullPath.startsWith('//')) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(fullPath, 'http://x');
  } catch {
    return null;
  }
  if (url.host !== 'x') return null;
  for (const p of SIGN_IN_PARAMS) url.searchParams.delete(p);
  const path = url.pathname.replace(/\/+$/, '') || '/';
  const query = url.searchParams.toString();
  // The default landing with nothing on it is where they would arrive anyway.
  if ((path === '/' || path === '/dashboard') && !query) return null;
  if (path === '/403') return null;
  return path + (query ? `?${query}` : '') + url.hash;
};

export const rememberReturnLink = (fullPath: unknown, now: number = Date.now()): boolean => {
  const link = cleanReturnLink(fullPath);
  if (!link) return false;
  try {
    sessionStorage.setItem(RETURN_LINK_KEY, JSON.stringify({ link, at: now }));
    return true;
  } catch {
    return false; // storage unavailable: they land on the default page as before
  }
};

export const forgetReturnLink = (): void => {
  try {
    sessionStorage.removeItem(RETURN_LINK_KEY);
  } catch {
    /* nothing to forget */
  }
};

/** The remembered link, once. Null when there is none, it is stale or unreadable. */
export const takeReturnLink = (now: number = Date.now()): string | null => {
  try {
    const raw = sessionStorage.getItem(RETURN_LINK_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RETURN_LINK_KEY);
    const saved = JSON.parse(raw) as { link?: unknown; at?: unknown };
    if (typeof saved.at !== 'number' || now - saved.at > RETURN_LINK_TTL_MS || now < saved.at) {
      return null;
    }
    return cleanReturnLink(saved.link);
  } catch {
    return null;
  }
};
